import { useState, useEffect } from 'react'
import { useProject } from '@/contexts/project-context'
import { EquityCalculationEngine } from '@/lib/equity-calculator-v2'
import { EquityCalculationResult, EquityWeightConfig } from '@/types/equity-calculator'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { Slider } from '@/components/ui/slider'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { 
  Calculator, 
  Users, 
  TrendingUp, 
  Download,
  RefreshCw,
  Settings,
  History,
  DollarSign
} from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { createClerkSupabaseClient } from '@/lib/supabase'
import { supabase } from '@/lib/supabase-auth'
import { useGetToken } from '@/lib/hooks/use-auth'
import { usePermissions } from '@/lib/hooks'
import { toast } from 'sonner'

export default function EquityRecommendationsPage() {
  const getToken = useGetToken()
  const { currentProject } = useProject()
  const permissions = usePermissions()
  const [calculationResult, setCalculationResult] = useState<EquityCalculationResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [config, setConfig] = useState<EquityWeightConfig | null>(null)
  const [totalShares, setTotalShares] = useState(10000000)
  const [optionPool, setOptionPool] = useState(15)
  const [history, setHistory] = useState<any[]>([])
  const [showConfig, setShowConfig] = useState(false)

  useEffect(() => {
    if (currentProject) {
      loadProjectConfig()
      loadCalculationHistory()
    }
  }, [currentProject])

  const loadProjectConfig = async () => {
    if (!currentProject) return

    // Create authenticated Supabase client with Clerk token
      const authenticatedClient = createClerkSupabaseClient(getToken)
      
      const { data, error } = await authenticatedClient
      .from('projects')
      .select('equity_config')
      .eq('id', currentProject.id)
      .single()

    if (data?.equity_config) {
      setConfig(data.equity_config as unknown as EquityWeightConfig)
    }
  }

  const loadCalculationHistory = async () => {
    if (!currentProject) return

    // Create authenticated Supabase client with Clerk token
    const authenticatedClient = createClerkSupabaseClient(getToken)

    const { data, error } = await authenticatedClient
      .from('equity_calculation_history')
      .select('*')
      .eq('project_id', currentProject.id)
      .order('calculated_at', { ascending: false })
      .limit(10)

    if (data) {
      setHistory(data.filter(h => !(h.result as any)?.pending))
    }
  }

  const runCalculation = async () => {
    if (!currentProject || !config) return

    setLoading(true)
    try {
      const engine = new EquityCalculationEngine(currentProject.id, config)
      const result = await engine.calculateEquityAllocations(totalShares, optionPool, true)
      
      setCalculationResult(result)
      
      // Save to history
      await engine.saveCalculationHistory(result, 'manual', 'User-triggered calculation')
      
      toast.success('Equity calculation completed')
      loadCalculationHistory()
    } catch (error) {
      console.error('Calculation error:', error)
      toast.error('Failed to calculate equity allocations')
    } finally {
      setLoading(false)
    }
  }

  const applyRecommendations = async () => {
    if (!currentProject || !calculationResult || !permissions.canEditProject) return

    try {
      const engine = new EquityCalculationEngine(currentProject.id, config!)
      await engine.updateCapTable(calculationResult.allocations)
      
      toast.success('Cap table updated with recommendations')
    } catch (error) {
      console.error('Error applying recommendations:', error)
      toast.error('Failed to update cap table')
    }
  }

  const updateConfig = (updates: Partial<EquityWeightConfig>) => {
    if (!config) return
    const newConfig = { ...config, ...updates }
    setConfig(newConfig)
  }

  const saveConfig = async () => {
    if (!currentProject || !config) return

    const { error } = await supabase
      .from('projects')
      .update({ equity_config: config as unknown as import('@/database.types').Json })
      .eq('id', currentProject.id)

    if (error) {
      toast.error('Failed to save configuration')
    } else {
      toast.success('Configuration saved')
    }
  }

  const exportResults = () => {
    if (!calculationResult) return

    const csvContent = [
      ['Name', 'Contribution Score', 'Equity %', 'Shares'].join(','),
      ...calculationResult.allocations.map(a => 
        [a.user_name, a.contribution_score, a.equity_percentage.toFixed(4), a.shares].join(',')
      )
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `equity-recommendations-${new Date().toISOString().split('T')[0]}.csv`
    link.click()
  }

  if (!config) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Equity Recommendations</h1>
          <p className="text-gray-600 mt-1">Dynamic equity allocation based on contributions</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowConfig(!showConfig)}
          >
            <Settings className="h-4 w-4 mr-2" />
            Configure
          </Button>
          <Button onClick={runCalculation} disabled={loading}>
            {loading ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Calculator className="h-4 w-4 mr-2" />
            )}
            Calculate
          </Button>
        </div>
      </div>

      {/* Configuration Panel */}
      {showConfig && (
        <Card>
          <CardHeader>
            <CardTitle>Equity Calculation Configuration</CardTitle>
            <CardDescription>
              Adjust weights and multipliers for equity calculations
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="font-semibold">Basic Parameters</h3>
                <div>
                  <Label>Total Shares</Label>
                  <Input
                    type="number"
                    value={totalShares}
                    onChange={(e) => setTotalShares(parseInt(e.target.value) || 10000000)}
                  />
                </div>
                <div>
                  <Label>Option Pool %</Label>
                  <div className="flex items-center gap-4">
                    <Slider
                      value={[optionPool]}
                      onValueChange={([v]) => setOptionPool(v)}
                      min={0}
                      max={30}
                      step={1}
                      className="flex-1"
                    />
                    <span className="w-12 text-right">{optionPool}%</span>
                  </div>
                </div>
                <div>
                  <Label>Founder Bonus</Label>
                  <div className="flex items-center gap-4">
                    <Slider
                      value={[(config.founder_bonus - 1) * 100]}
                      onValueChange={([v]) => updateConfig({ founder_bonus: 1 + v / 100 })}
                      min={0}
                      max={50}
                      step={5}
                      className="flex-1"
                    />
                    <span className="w-12 text-right">+{Math.round((config.founder_bonus - 1) * 100)}%</span>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold">T-Shirt Size Weights</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm">Effort Weights</Label>
                    {Object.entries(config.effort_weights).map(([size, weight]) => (
                      <div key={size} className="flex items-center gap-2 mt-2">
                        <Badge variant="outline" className="w-8">{size}</Badge>
                        <Input
                          type="number"
                          value={weight}
                          onChange={(e) => updateConfig({
                            effort_weights: { ...config.effort_weights, [size]: parseInt(e.target.value) || 1 }
                          })}
                          className="w-20"
                        />
                      </div>
                    ))}
                  </div>
                  <div>
                    <Label className="text-sm">Capital Weights</Label>
                    {Object.entries(config.capital_weights).map(([size, weight]) => (
                      <div key={size} className="flex items-center gap-2 mt-2">
                        <Badge variant="outline" className="w-8">{size}</Badge>
                        <Input
                          type="number"
                          value={weight}
                          onChange={(e) => updateConfig({
                            capital_weights: { ...config.capital_weights, [size]: parseInt(e.target.value) || 1 }
                          })}
                          className="w-20"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <Button onClick={saveConfig} variant="outline">
                Save Configuration
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {calculationResult && (
        <Tabs defaultValue="allocations" className="space-y-4">
          <TabsList>
            <TabsTrigger value="allocations">Allocations</TabsTrigger>
            <TabsTrigger value="summary">Summary</TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
          </TabsList>

          <TabsContent value="allocations" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Recommended Equity Allocations</CardTitle>
                    <CardDescription>
                      Based on {calculationResult.allocations.length} contributors
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={exportResults}>
                      <Download className="h-4 w-4 mr-2" />
                      Export CSV
                    </Button>
                    {permissions.canEditProject && (
                      <Button size="sm" onClick={applyRecommendations}>
                        Apply to Cap Table
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Contributor</TableHead>
                        <TableHead className="text-right">Score</TableHead>
                        <TableHead className="text-right">Equity %</TableHead>
                        <TableHead className="text-right">Shares</TableHead>
                        <TableHead className="text-right">Value @ $1M</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {calculationResult.allocations.map((allocation) => (
                        <TableRow key={allocation.user_id}>
                          <TableCell className="font-medium">{allocation.user_name}</TableCell>
                          <TableCell className="text-right">
                            {allocation.contribution_score.toFixed(0)}
                          </TableCell>
                          <TableCell className="text-right">
                            {allocation.equity_percentage.toFixed(4)}%
                          </TableCell>
                          <TableCell className="text-right">
                            {allocation.shares.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right">
                            ${(allocation.equity_percentage * 10000).toLocaleString()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="summary" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Founder Ownership</CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {calculationResult.founder_ownership.toFixed(1)}%
                  </div>
                  <p className="text-xs text-muted-foreground">
                    After dilution protection
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Investor Ownership</CardTitle>
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {calculationResult.investor_ownership.toFixed(1)}%
                  </div>
                  <p className="text-xs text-muted-foreground">
                    From funding rounds
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Option Pool</CardTitle>
                  <TrendingUp className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {calculationResult.option_pool}%
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Reserved for future grants
                  </p>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Calculation Details</CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <dt className="text-gray-500">Total Shares</dt>
                    <dd className="font-medium">{calculationResult.total_shares.toLocaleString()}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">Total Score</dt>
                    <dd className="font-medium">{calculationResult.total_score.toFixed(0)}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">Calculated At</dt>
                    <dd className="font-medium">
                      {new Date(calculationResult.calculated_at).toLocaleString()}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">Contributors</dt>
                    <dd className="font-medium">{calculationResult.allocations.length}</dd>
                  </div>
                </dl>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="history">
            <Card>
              <CardHeader>
                <CardTitle>Calculation History</CardTitle>
                <CardDescription>
                  Previous equity calculations for this project
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Triggered By</TableHead>
                        <TableHead className="text-right">Contributors</TableHead>
                        <TableHead className="text-right">Founder %</TableHead>
                        <TableHead>Notes</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {history.map((entry) => (
                        <TableRow key={entry.id}>
                          <TableCell>
                            {new Date(entry.calculated_at).toLocaleString()}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{entry.triggered_by}</Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            {entry.result.allocations?.length || 0}
                          </TableCell>
                          <TableCell className="text-right">
                            {entry.result.founder_ownership?.toFixed(1) || 0}%
                          </TableCell>
                          <TableCell className="text-sm text-gray-500">
                            {entry.notes || '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  )
}
